import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import DOMPurify from 'dompurify';
import { PhotoIcon } from '@heroicons/react/20/solid';
import '../assets/fonts/poppins.css';

import AssembloLogo from "../assets/img/assemblo-logo.svg";
import Facebook from "../assets/img/waiting/facebook.svg";
import Instagram from "../assets/img/waiting/instagram.svg";
import LinkedIn from "../assets/img/waiting/linkedin.svg";
import YouTube from "../assets/img/waiting/youtube.svg";

// import services
import WaitingScreenService from "../services/waitingscreen.service";

const SOCIALS = [
    { name: "Facebook", icon: Facebook, url: "https://www.facebook.com/upgradeestate/" },
    { name: "Instagram", icon: Instagram, url: "https://www.instagram.com/upgradeestate/" },
    { name: "LinkedIn", icon: LinkedIn, url: "https://linkedin.com/company/upgrade-estate" },
    { name: "YouTube", icon: YouTube, url: "https://www.youtube.com/channel/UCyvSsxnYmGCBKcVUXG6u2mA" },
];

// Up to two project cards. The second card uses the same fields with a "card2_" prefix,
// and each card has its own switch.
const CARDS = [
    { prefix: "", activeKey: "active" },
    { prefix: "card2_", activeKey: "card2_active" },
];

function activeCards(screen, language, preview) {
    if (!screen) return [];
    return CARDS.filter(({ activeKey }) => screen[activeKey]).map(({ prefix }) => {
        const text = (field) => screen[`${prefix}${field}_${language}`] || "";
        const image = screen[`${prefix}image`];
        return {
            key: prefix || "card1",
            image: image ? (preview ? image : process.env.REACT_APP_API_URI + "file/download/" + image) : null,
            pretitle: text("pretitle"),
            title: text("title"),
            subtitle: text("subtitle"),
            buttonText: text("button_text"),
            buttonUrl: screen[`${prefix}button_url`],
        };
    });
}

function ProjectCard({ card, preview, className = "" }) {
    return (
        <div className={`flex w-5/6 rounded-[2.5rem] bg-white text-left text-[#321548] ${className}`}>
            <div className="m-6 w-1/2 flex-shrink-0">
                {card.image ? (
                    <img src={card.image} alt="" className="h-[16.5rem] w-full rounded-[2rem] object-cover" />
                ) : (
                    <div className="flex h-[16.5rem] w-full items-center justify-center rounded-[2rem] bg-gray-200">
                        <PhotoIcon className="h-16 w-16 text-gray-400" aria-hidden="true" />
                    </div>
                )}
            </div>
            <div className="my-auto min-w-0 flex-1 py-6 pl-6 pr-8">
                {card.pretitle ? <div className="mb-1 text-base">{card.pretitle}</div> : null}
                <h3 className="text-[2rem] font-bold leading-tight">{card.title}</h3>
                <div
                    className="mt-5 text-base leading-6"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(card.subtitle) }}
                />
                <button
                    type="button"
                    className="mt-5 inline-flex min-h-[3.25rem] items-center rounded-2xl bg-[#800080] px-8 py-3 text-base font-bold text-white"
                    onClick={() => {
                        if (!preview && card.buttonUrl) window.open(card.buttonUrl, "_blank");
                    }}
                >
                    {card.buttonText}
                </button>
            </div>
        </div>
    );
}

// Props
//   date        meeting start, drives the countdown
//   lang        "NL" | "FR" | "EN"
//   preview     render with the data in `previewData` instead of fetching the
//               active waiting screen, and size to the parent instead of the
//               viewport (used by the admin editor)
//   previewData waiting screen fields; image fields are ready-to-use URLs or null
export default function WaitingComponent({ date, lang, preview = false, previewData = null }) {
    const { t: tDefault, i18n } = useTranslation();
    const language = (lang || "nl").toLowerCase();
    const t = preview ? i18n.getFixedT(language) : tDefault;

    const [timeUntilMeeting, setTimeUntilMeeting] = useState(getTimeUntilMeeting(date));
    const [fetchedScreen, setFetchedScreen] = useState(null);

    useEffect(() => {
        if (preview) return;
        WaitingScreenService.get()
            .then((response) => {
                // Only set when at least one card is switched on
                if (response.data && (response.data.active || response.data.card2_active)) {
                    setFetchedScreen(response.data);
                }
            })
            .catch(() => {
                // No waiting screen configured - show the countdown only
                setFetchedScreen(null);
            });
    }, [preview]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTimeUntilMeeting(getTimeUntilMeeting(date));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [date]);

    const cards = activeCards(preview ? previewData : fetchedScreen, language, preview);
    const pad = (value) => value.toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });

    return (
        <div className="dark-no-bg font-poppins" style={preview ? { minHeight: 0, height: "100%", paddingBottom: 0 } : { paddingBottom: "0" }}>
            <div className={`flex flex-col ${preview ? "h-full" : "h-screen"}`}>
                <div className="flex min-h-0 flex-1 pt-24">
                    <div className="my-auto w-5/12 text-white">
                        <img src={AssembloLogo} alt="Assemblo logo" className="mx-auto w-[20rem]" />
                        <div className="mt-14 text-[40px] font-bold leading-tight">{t("meeting.start.patience")}</div>
                        <div className="mt-3 text-2xl font-light">{t("meeting.start.soon")}</div>
                        <div className="mt-8 text-2xl font-bold">
                            {timeUntilMeeting.days !== 0 && (
                                <>
                                    {timeUntilMeeting.days} {timeUntilMeeting.days > 1 ? t("meeting.start.days") : t("meeting.start.day")} {t("meeting.start.and")}
                                </>
                            )}
                        </div>
                        <div className="text-[84px] font-bold leading-tight">
                            {pad(timeUntilMeeting.hours)}:{pad(timeUntilMeeting.minutes)}:{pad(timeUntilMeeting.seconds)}
                        </div>
                    </div>
                    <div className="my-auto w-7/12 pr-20">
                        {cards.map((card, index) => (
                            <ProjectCard
                                key={card.key}
                                card={card}
                                preview={preview}
                                className={index > 0 ? "ml-auto mt-10" : ""}
                            />
                        ))}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-10 pb-12 pr-20 text-white">
                    <span className="text-xl font-semibold">
                        {t("meeting.start.advertising.2.title")} {t("meeting.start.advertising.2.project")}
                    </span>
                    <div className="flex items-center gap-5">
                        {SOCIALS.map((social) => (
                            <a
                                key={social.name}
                                href={social.url}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={social.name}
                                onClick={preview ? (e) => e.preventDefault() : undefined}
                            >
                                <img src={social.icon} alt="" width={32} height={32} className="h-8 w-8 brightness-0 invert" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function getTimeUntilMeeting(meetingDatetime) {
    const currentTime = new Date();
    const meetingTime = new Date(meetingDatetime);
    const timeDifference = meetingTime - currentTime;

    const secondsUntilMeeting = Math.floor(timeDifference / 1000);
    const minutesUntilMeeting = Math.floor(secondsUntilMeeting / 60);
    const hoursUntilMeeting = Math.floor(minutesUntilMeeting / 60);
    const daysUntilMeeting = Math.floor(hoursUntilMeeting / 24);

    return {
        days: daysUntilMeeting > 0 ? daysUntilMeeting : 0,
        hours: hoursUntilMeeting % 24 > 0 ? hoursUntilMeeting % 24 : 0,
        minutes: minutesUntilMeeting % 60 > 0 ? minutesUntilMeeting % 60 : 0,
        seconds: secondsUntilMeeting % 60 > 0 ? secondsUntilMeeting % 60 : 0,
    };
}
